import BrandHeader from "../components/BrandHeader";
import { Card, CardContent } from "../components/ui/card";

const userLenses = [
  {
    title: "1. Donors — CSR and non-CSR funders",
    body: [
      "Two jobs here: build sector understanding, and find credible implementing partners.",
      "What you get today: the sector view — money flow by geography, domain, company, year, and ticket size. Useful for portfolio planning, diligence benchmarking, and spotting white-space before a new grant cycle.",
      "What you do not get yet: the implementing-agency directory. MCA filings do not expose implementing-agency names at the dataset level — pulling them requires scraping individual company filings one at a time, which is resource-heavy and invites data-quality risk. Our partner-discovery capability on this platform is therefore limited today; we are transparent about that because over-claiming here damages trust with both sides of the market.",
    ],
  },
  {
    title: "2. Grant-seekers — NGOs and implementing agencies",
    body: [
      "If you work on a defined geography and a defined domain, your core question is: who is funding this, and how do I reach them?",
      "What you get today: CSR flows filtered by geography, domain, year, and ticket size. Every row is auditable back to a CSR-1 filing. v1 gives you the funder map.",
      "What you do not get yet: peer reference — which NGOs of your size and DNA have won grants from which funders. That closes the loop, and depends on the same implementing-agency directory the donor lens needs.",
    ],
  },
  {
    title: "3. Researchers, consultants, academia, and government",
    body: [
      "Structured, downloadable CSR data for sector studies, policy work, sourcing, and program design.",
      "Scope and access model for this lens is still being finalized.",
    ],
  },
];

const guardrails = [
  "We will not scrape. Everything here is MCA data — authentic, traceable, auditable.",
  "We will not infer with AI. If we do not have a signal, we will not fabricate one.",
  "We will not over-claim non-CSR coverage. Philanthropy and non-CSR giving is the bigger gap in the sector; we will say so clearly until we have something real to show.",
];

const laneCards = [
  {
    title: "Lane 1 — RTI to MCA",
    text: "The cleanest structural fix is an RTI requesting that implementing-agency names be published alongside each CSR grant, tagged with PAN / DIN / CSR-1 reference. That unlocks cross-referencing for everyone without anyone needing to scrape.",
  },
  {
    title: "Lane 2 — partnership with Sattva",
    text: "Sattva has done substantive work in this space already, and we have an existing partnership with them. We would like to build the next version jointly rather than in parallel.",
  },
];

export default function AboutUsPage() {
  return (
    <div className="site-shell min-h-screen">
      <BrandHeader compact />

      <main className="mx-auto flex w-full max-w-[1700px] flex-col gap-8 px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pt-10">
        <section className="section-frame space-y-6 p-6 md:p-10" data-testid="about-us-hero-section">
          <div className="hero-line" />
          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">About Us</p>
            <h1 className="max-w-5xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="about-us-heading">
              Yet another CSR navigator
            </h1>
            <p className="text-base font-semibold text-[#A31D2F]" data-testid="about-us-subheading">by Dhwani RIS</p>
            <p className="max-w-4xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Dhwani RIS has been building data infrastructure for India&apos;s impact sector since 2015. This navigator is our attempt to point that muscle at a public utility — honest about what it does well, and equally honest about what it does not solve yet.
            </p>
          </div>
        </section>

        <section className="section-frame space-y-6 p-6 md:p-8" data-testid="about-origin-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">The origin note</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Why this exists now.</h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4 text-sm leading-relaxed text-slate-700">
              <p>
                A few weeks ago, Ms. Mamta Bhandari put out a vibe-coded CSR navigator on LinkedIn, stitched together from multiple public sources. It crossed ~8,000 visits in a short window. Several people in our network pinged us to ask whether the data held up, whether anything like this had existed before, and whether we had thought of building one.
              </p>
              <p>
                The honest answer was: no, and we should have. Special thanks to Mamta — without her push, we would not have attempted this.
              </p>
              <p>
                The name is deliberate. This is yet another CSR navigator, not the CSR navigator. This space needs multiple honest attempts before anything definitive emerges, and we would rather add one more useful tool to the pile than claim to have solved it.
              </p>
            </div>
            <Card className="rounded-none border-slate-200 bg-slate-50">
              <CardContent className="space-y-4 p-6">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">Build posture</p>
                <p className="text-sm leading-relaxed text-slate-700">
                  This is our first public attempt in the space. We would rather release something useful, auditable, and incomplete than over-claim certainty in a fragmented ecosystem.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="section-frame space-y-6 p-6 md:p-8" data-testid="about-why-hard-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Why this is hard</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Grant data in India is fragmented.</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <Card className="rounded-none border-slate-200 bg-white">
              <CardContent className="space-y-3 p-6">
                <p className="text-lg font-bold text-slate-950">CSR</p>
                <p className="text-sm leading-relaxed text-slate-700">
                  Traceable through MCA filings, but published closer to raw compliance data than a usable dataset.
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-none border-slate-200 bg-white">
              <CardContent className="space-y-3 p-6">
                <p className="text-lg font-bold text-slate-950">Non-CSR</p>
                <p className="text-sm leading-relaxed text-slate-700">
                  Domestic philanthropy, family foundations, bilateral and multilateral grants, HNI and retail giving. Largely opaque, scattered across annual reports, press releases, and relationships.
                </p>
              </CardContent>
            </Card>
          </div>
          <p className="text-sm leading-relaxed text-slate-700">
            This navigator addresses the first, reasonably well. The second, we have not solved yet — our most plausible route in is through our own mGrant partner network, subject to consent.
          </p>
        </section>

        <section className="section-frame space-y-6 p-6 md:p-8" data-testid="about-user-lenses-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Who this is for</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Three user lenses shaped this navigator.</h2>
          </div>
          <div className="grid gap-5 xl:grid-cols-3">
            {userLenses.map((lens) => (
              <Card key={lens.title} className="rounded-none border-slate-200 bg-white">
                <CardContent className="space-y-4 p-6">
                  <h3 className="text-xl font-bold text-slate-950">{lens.title}</h3>
                  <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                    {lens.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="section-frame space-y-6 p-6 md:p-8" data-testid="about-guardrails-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">What we will not do</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Clear guardrails matter.</h2>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {guardrails.map((item) => (
              <Card key={item} className="rounded-none border-slate-200 bg-slate-50">
                <CardContent className="p-5 text-sm leading-relaxed text-slate-700">{item}</CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="section-frame space-y-6 p-6 md:p-8" data-testid="about-mgrant-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">The data we have, and haven&apos;t used yet</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">The mGrant network is meaningful — and consent still comes first.</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <Card className="rounded-none border-slate-200 bg-white">
              <CardContent className="space-y-4 p-6">
                <div>
                  <p className="text-3xl font-black text-slate-950">40+</p>
                  <p className="text-sm text-slate-600">donor organisations</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-slate-950">₹15,000+ Cr</p>
                  <p className="text-sm text-slate-600">in grants channelized annually</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-slate-950">~2,000</p>
                  <p className="text-sm text-slate-600">unique non-profit grantees</p>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4 text-sm leading-relaxed text-slate-700">
              <p>
                Dhwani operates mGrant — a grant management platform used by donors and funders to run their giving. That is a non-trivial view of Indian grantmaking, including segments of the non-CSR world that are otherwise invisible. We have deliberately not used it here.
              </p>
              <p>
                Two reasons. First, the data belongs to our partners, not to us. Some of it is already in the public domain through their own reporting; we still have not used it, because aggregating partner data into a public utility without their explicit sign-off is not a shortcut we are willing to take.
              </p>
              <p>
                Second, a consent-first posture is the only way this ecosystem compounds. What we plan to do: speak to each mGrant partner, understand what they are willing to put in the public domain, and release only what they authorise — attributed, auditable, and useful.
              </p>
            </div>
          </div>
        </section>

        <section className="section-frame space-y-6 p-6 md:p-8" data-testid="about-ecosystem-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">The ecosystem play</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Two lanes, because the gap is sector-level.</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            {laneCards.map((lane) => (
              <Card key={lane.title} className="rounded-none border-slate-200 bg-white">
                <CardContent className="space-y-3 p-6">
                  <h3 className="text-xl font-bold text-slate-950">{lane.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-700">{lane.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-sm leading-relaxed text-slate-700">We welcome partners who want to push on either lane with us.</p>
        </section>

        <section className="section-frame space-y-4 p-6 md:p-8" data-testid="about-credits-section">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">Credits</p>
          <div className="space-y-2 text-sm text-slate-700">
            <p><span className="font-semibold text-slate-950">Inspiration:</span> Ms. Mamta Bhandari, whose LinkedIn post lit the fuse.</p>
            <p><span className="font-semibold text-slate-950">Data:</span> Ministry of Corporate Affairs (CSR filings).</p>
            <p><span className="font-semibold text-slate-950">Build:</span> Dhwani RIS — building impact-sector data infrastructure since 2015.</p>
          </div>
        </section>
      </main>
    </div>
  );
}